import { collection, doc } from "firebase/firestore";
import { ref, updateMetadata, uploadBytes } from "firebase/storage";
import { AlertCircle, FileText, Trash2, Upload, Users } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { EmptyState, LoadingCards } from "components/states";
import { ToggleChip } from "components/ToggleChip";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { FileInput } from "components/ui/file-input";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { db } from "integrations/firebase/firestore";
import { storage } from "integrations/firebase/storage";

import { AdminCommandFeedback } from "../AdminCommandFeedback";
import { useAdminCommand } from "../use-admin-command";
import { useAdminResources, useAdminTracks } from "../use-admin-content";
import type { Resource, Track } from "../../event-experience/schemas";
import {
  audienceFor,
  audienceMetadata,
  describeAudience,
  liveTrackIds,
  MAX_RESOURCE_TRACKS,
} from "../../event-experience/resource-audience";
import { resourceTypeLabel } from "../../event-experience/resource-type";
import {
  ResourceFailureScope,
  ResourceOpenControl,
  useResourceDownload,
} from "../../event-experience/ResourceOpenLink";
import { useEntityWrites } from "../use-entity-writes";

const MAX_BYTES = 20 * 1024 * 1024;

/** Exactly the set firestore.rules accepts for a resource. */
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
];

/**
 * Resources are the one content collection that is a file, not a form.
 *
 * The ordering is forced by the rules: `storagePath` must match
 * `events/<eventId>/resources/<resourceId>/<filename>`, so the document id has
 * to exist before the upload can be addressed. The id is therefore minted
 * client-side first, the bytes go to Storage, and only then is the document
 * written — which is also why a failed upload leaves nothing behind.
 *
 * A resource can be limited to tracks. The choice is written twice: on the
 * document, where firestore.rules filters the attendee listing, and on the
 * Storage object's metadata, where storage.rules guards the download. The
 * object is always written first, so a save that fails halfway leaves the
 * file at the new setting and the listing at the old one.
 *
 * TNG-96: a resource can also be marked "after the event", which lists it in
 * the attendee's "After the event" section as well as the Resources tab. It
 * changes who sees it not at all, so it lives only on the document.
 */
export function ResourcesSection({
  eventId,
  uid,
}: {
  eventId: string;
  uid: string;
}) {
  const resources = useAdminResources(eventId);
  const tracks = useAdminTracks(eventId);
  // Until the tracks have loaded, nothing may be read from an empty list: a
  // restricted resource would show as admins only, and saving its tracks
  // would open it to everyone. So the pickers and the Tracks button wait.
  const tracksReady = tracks.status === "success";
  const tracksError = tracks.status === "error" ? tracks.error : null;
  const trackList = tracks.status === "success" ? tracks.data : [];
  const trackNames = useMemo(
    () => new Map(trackList.map((track) => [track.id, track.name])),
    [trackList],
  );
  const writes = useEntityWrites(eventId, "resources");
  const command = useAdminCommand();
  const fileInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [trackIds, setTrackIds] = useState<string[]>([]);
  const [afterEvent, setAfterEvent] = useState(false);
  const [savingAfterEvent, setSavingAfterEvent] = useState<string | null>(null);
  // What the organiser last set, shown at once so the box follows the tap
  // rather than the reload. Dropped again when the write is refused.
  const [afterEventMarks, setAfterEventMarks] = useState<
    Record<string, boolean>
  >({});
  const [editing, setEditing] = useState<{
    resource: Resource;
    trackIds: string[];
  } | null>(null);
  const [savingAudience, setSavingAudience] = useState(false);
  const [audienceError, setAudienceError] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Resource | null>(null);

  const busy =
    uploading ||
    savingAudience ||
    command.state.status === "submitting" ||
    command.state.status === "watching";

  useEffect(() => {
    if (command.state.status === "succeeded") {
      setPendingDelete(null);
      resources.reload();
    }
  }, [command.state.status, resources.reload]);

  async function upload() {
    if (!file) {
      setProblem("Choose a file to upload.");
      return;
    }
    if (!name.trim()) {
      setProblem("Name is required.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setProblem("Files must be 20 MB or smaller.");
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      setProblem("That file type is not accepted.");
      return;
    }
    setProblem(null);
    setUploading(true);
    try {
      // Mint the id first: the storage path is anchored to it.
      const resourceRef = doc(collection(db, "events", eventId, "resources"));
      const storagePath = `events/${eventId}/resources/${resourceRef.id}/${file.name}`;
      const audience = audienceFor(trackIds.filter((id) => trackNames.has(id)));
      await uploadBytes(ref(storage, storagePath), file, {
        contentType: file.type,
        customMetadata: audienceMetadata(audience),
      });
      const created = await writes.create(
        {
          name: name.trim(),
          ...(description.trim() ? { description: description.trim() } : {}),
          storagePath,
          size: file.size,
          contentType: file.type,
          uploadedBy: uid,
          ...audience,
          ...(afterEvent ? { afterEvent: true } : {}),
        },
        { timestampFields: ["uploadedAt"], id: resourceRef.id },
      );
      if (created) {
        setName("");
        setDescription("");
        setFile(null);
        setTrackIds([]);
        setAfterEvent(false);
        if (fileInput.current) fileInput.current.value = "";
        resources.reload();
      }
    } catch (err) {
      setProblem(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  async function saveAudience() {
    if (!editing || !tracksReady) return;
    const audience = audienceFor(
      editing.trackIds.filter((id) => trackNames.has(id)),
    );
    setAudienceError(null);
    setSavingAudience(true);
    try {
      // The file first: see the component comment.
      await updateMetadata(ref(storage, editing.resource.storagePath), {
        customMetadata: audienceMetadata(audience),
      });
      const saved = await writes.update(editing.resource.id, audience);
      if (saved) {
        setEditing(null);
        resources.reload();
      } else {
        setAudienceError(
          "The file now has the new setting, but the list entry could not be updated. Save again.",
        );
      }
    } catch (err) {
      setAudienceError(
        err instanceof Error ? err.message : "Could not change who sees it.",
      );
    } finally {
      setSavingAudience(false);
    }
  }

  async function saveAfterEvent(resource: Resource, next: boolean) {
    setSavingAfterEvent(resource.id);
    setAfterEventMarks((marks) => ({ ...marks, [resource.id]: next }));
    try {
      if (await writes.update(resource.id, { afterEvent: next })) {
        resources.reload();
      } else {
        setAfterEventMarks(({ [resource.id]: _, ...rest }) => rest);
      }
    } finally {
      setSavingAfterEvent(null);
    }
  }

  if (resources.status === "loading") return <LoadingCards cards={3} />;
  if (resources.status === "error")
    return (
      <EmptyState icon={AlertCircle} title="Could not load resources">
        {resources.error}
      </EmptyState>
    );

  return (
    <div className="space-y-4" data-testid="admin-resources-content">
      <Badge variant="outline">
        {resources.data.length}{" "}
        {resources.data.length === 1 ? "resource" : "resources"}
      </Badge>

      <Card className="border-border shadow-none">
        <CardContent className="space-y-3 pt-6">
          <div className="space-y-1.5">
            <Label htmlFor="resource-name">Name *</Label>
            <Input
              id="resource-name"
              testId="resource-name"
              value={name}
              disabled={busy}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="resource-description">Description</Label>
            <Input
              id="resource-description"
              testId="resource-description"
              value={description}
              disabled={busy}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="resource-file">File *</Label>
            <FileInput
              id="resource-file"
              ref={fileInput}
              testId="resource-file"
              disabled={busy}
              accept={ALLOWED_TYPES.join(",")}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <p className="text-xs text-muted-foreground">
              Documents up to 20 MB. PDF, Word, Excel, PowerPoint or plain text.
            </p>
          </div>
          <TrackAudiencePicker
            testId="resource-tracks"
            tracks={trackList}
            status={tracks.status}
            error={tracksError}
            selected={trackIds}
            disabled={busy}
            onChange={setTrackIds}
          />
          <AfterEventCheckbox
            id="resource-after-event"
            checked={afterEvent}
            disabled={busy}
            onChange={setAfterEvent}
          />
          {(problem || writes.error) && (
            <p
              className="text-sm text-destructive-text"
              data-testid="resource-error"
            >
              {problem ?? writes.error}
            </p>
          )}
          <Button
            variant="cta"
            onClick={upload}
            disabled={busy}
            data-testid="upload-resource"
          >
            <Upload className="h-4 w-4" />
            {uploading ? "Uploading…" : "Upload resource"}
          </Button>
        </CardContent>
      </Card>

      <AdminCommandFeedback
        state={command.state}
        onReset={command.reset}
        testId="resource-delete"
      />

      {resources.data.length === 0 ? (
        <EmptyState icon={FileText} title="No resources">
          Upload the documents participants need.
        </EmptyState>
      ) : (
        <ResourceFailureScope testId="resources-failed">
          <div className="space-y-2">
            {resources.data.map((resource) => (
              <Card key={resource.id} className="border-border shadow-none">
                <CardContent className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-foreground">
                      {resource.name}
                    </p>
                    <p className="truncate text-sm text-muted-foreground">
                      {[
                        resourceTypeLabel(resource.contentType),
                        formatSize(resource.size ?? 0),
                        resource.description,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <AudienceBadge
                      resource={resource}
                      trackNames={trackNames}
                      tracksReady={tracksReady}
                    />
                    <AfterEventCheckbox
                      id={`resource-after-event-${resource.id}`}
                      checked={
                        afterEventMarks[resource.id] ??
                        resource.afterEvent === true
                      }
                      disabled={busy || savingAfterEvent === resource.id}
                      onChange={(next) => void saveAfterEvent(resource, next)}
                      compact
                    />
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-11"
                      disabled={busy || !tracksReady}
                      data-testid={`edit-resource-audience-${resource.id}`}
                      aria-label={`Change who sees ${resource.name}`}
                      onClick={() => {
                        setAudienceError(null);
                        setEditing({
                          resource,
                          trackIds: liveTrackIds(resource, trackNames),
                        });
                      }}
                    >
                      <Users className="h-4 w-4" />
                      Tracks
                    </Button>
                    <AdminResourceOpen resource={resource} />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-11 w-11"
                      disabled={busy}
                      aria-label={`Delete ${resource.name}`}
                      data-testid={`delete-resource-${resource.id}`}
                      onClick={() => setPendingDelete(resource)}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </ResourceFailureScope>
      )}

      <Dialog
        open={editing !== null}
        onOpenChange={(next) => !next && setEditing(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Who sees {editing?.resource.name}?</DialogTitle>
          </DialogHeader>
          <TrackAudiencePicker
            testId="resource-audience-tracks"
            tracks={trackList}
            status={tracks.status}
            error={tracksError}
            selected={editing?.trackIds ?? []}
            disabled={savingAudience}
            onChange={(next) =>
              setEditing((current) =>
                current ? { ...current, trackIds: next } : current,
              )
            }
          />
          <p className="text-xs text-muted-foreground">
            A download link someone already opened keeps working after you
            narrow this. The setting controls who can find and open the file
            from now on, and cannot recall a copy or a forwarded link.
          </p>
          {audienceError && (
            <p
              className="text-sm text-destructive-text"
              role="alert"
              data-testid="resource-audience-error"
            >
              {audienceError}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button
              variant="cta"
              disabled={savingAudience || !tracksReady}
              data-testid="save-resource-audience"
              onClick={() => void saveAudience()}
            >
              {savingAudience ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(next) => !next && setPendingDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete resource?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {pendingDelete?.name} will be removed, and so will the uploaded
            file. Anyone holding a download link loses it.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              data-testid="confirm-delete-resource"
              onClick={() => {
                if (!pendingDelete) return;
                void command.submit("DELETE_RESOURCE", eventId, uid, {
                  resourceId: pendingDelete.id,
                });
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * TNG-96. 44px tall on a phone, like the other controls here, because the
 * label is part of the target.
 */
function AfterEventCheckbox({
  id,
  checked,
  disabled,
  onChange,
  compact = false,
}: {
  id: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
  compact?: boolean;
}) {
  return (
    <label
      htmlFor={id}
      className={`flex min-h-11 items-center gap-2 text-foreground sm:min-h-0 ${
        compact ? "text-xs" : "text-sm"
      }`}
    >
      <input
        id={id}
        type="checkbox"
        className="size-4 accent-lit"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={id}
      />
      {compact
        ? "Also in After the event"
        : "Also show in the attendees' After the event section"}
    </label>
  );
}

/** No track selected means every attendee sees the resource. */
function TrackAudiencePicker({
  testId,
  tracks,
  status,
  error,
  selected,
  disabled,
  onChange,
}: {
  testId: string;
  tracks: Track[];
  status: "loading" | "success" | "error";
  error: string | null;
  selected: string[];
  disabled: boolean;
  onChange: (next: string[]) => void;
}) {
  const atCap = selected.length >= MAX_RESOURCE_TRACKS;
  return (
    <div className="space-y-1.5" data-testid={testId}>
      <Label>Visible to tracks</Label>
      {status === "loading" ? (
        <p className="text-xs text-muted-foreground">Loading tracks…</p>
      ) : status === "error" ? (
        <p
          className="text-xs text-destructive-text"
          data-testid={`${testId}-error`}
        >
          Could not load the tracks{error ? ` (${error})` : ""}. Until they
          load, a new upload is visible to every attendee and tracks cannot be
          changed.
        </p>
      ) : tracks.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Every attendee sees this resource. To limit it, add tracks on the
          Tracks tab.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {tracks.map((track) => {
              const on = selected.includes(track.id);
              return (
                <ToggleChip
                  key={track.id}
                  selected={on}
                  disabled={disabled || (!on && atCap)}
                  data-testid={`${testId}-${track.id}`}
                  onClick={() =>
                    onChange(
                      on
                        ? selected.filter((id) => id !== track.id)
                        : [...selected, track.id],
                    )
                  }
                >
                  {track.name}
                </ToggleChip>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            {selected.length === 0
              ? "No track selected: every attendee sees it."
              : "Only attendees in the selected tracks see it. Event admins always do."}
          </p>
        </>
      )}
    </div>
  );
}

function AudienceBadge({
  resource,
  trackNames,
  tracksReady,
}: {
  resource: Resource;
  trackNames: ReadonlyMap<string, string>;
  tracksReady: boolean;
}) {
  if (!tracksReady && resource.audience === "tracks") {
    return (
      <p
        className="truncate text-xs text-muted-foreground"
        data-testid={`resource-audience-${resource.id}`}
      >
        Limited to tracks
      </p>
    );
  }
  const described = describeAudience(resource, trackNames);
  return (
    <p
      className="truncate text-xs text-muted-foreground"
      data-testid={`resource-audience-${resource.id}`}
    >
      {described.kind === "everyone"
        ? "All attendees"
        : described.kind === "admins"
          ? "Admins only: its tracks were deleted"
          : `Tracks: ${described.names.join(", ")}`}
    </p>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The admin row's Open control. The URL is fetched when the row renders and
 * kept in memory only; useResourceDownload explains why.
 */
function AdminResourceOpen({ resource }: { resource: Resource }) {
  const download = useResourceDownload(resource.storagePath);
  return (
    <>
      {download.status === "error" && (
        <span className="text-xs text-destructive-text">Could not load</span>
      )}
      <ResourceOpenControl
        download={download}
        name={resource.name}
        testId={`open-resource-${resource.id}`}
      />
    </>
  );
}
